import { isEmpty } from 'lodash';
import type { IWarehouse } from '@nova/core';

import apiClient from './apiClient';

export type AppointmentBolResponse = {
  id: string;
  originalDoc: {
    id: string;
    externalKey: string;
  };
  signedDoc: {
    id: string;
    externalKey: string;
  } | null;
};

export type ValidateCheckoutForm = {
  licensePlate?: string;
  phoneNumber?: string;
};

export type ValidateCheckoutFormResponse = {
  appointmentId: string;
  assetVisitId: string;
};

export type SignBolData = {
  appointmentId: string;
  driverFullName: string;
};

export type SendBolEmailSms = {
  warehouseId: string;
  appointmentId: string;
  contactData: string;
  type: 'email' | 'sms';
};

/** Single entry for custom forms (matches check-in / backend CreateCustomFormDataDto shape) */
export type CompleteCheckoutCustomFormEntry = {
  formFieldId: string;
  triggerId: string;
  objectId: string;
  entityName: string;
  warehouseId?: string;
  value?: unknown;
  id?: string;
};

export type CompleteCheckoutData = {
  assetVisitId: string;
  appointmentId?: string;
  customFormsData?: CompleteCheckoutCustomFormEntry[];
};

export default {
  async getWarehouseInformation(warehouseId: string): Promise<Partial<IWarehouse>> {
    const response = await apiClient.get(`/checkout/public/${warehouseId}`);
    return response.data;
  },

  async validateCheckoutForm(
    warehouseId: string,
    formData: ValidateCheckoutForm
  ): Promise<ValidateCheckoutFormResponse> {
    const response = await apiClient.post(`/checkout/public/${warehouseId}/validate`, formData);
    return response.data;
  },

  async getBol(warehouseId: string, appointmentId: string): Promise<AppointmentBolResponse> {
    const response = await apiClient.post(`/checkout/public/${warehouseId}/bol`, { appointmentId });
    return response.data;
  },

  async previewBolPdf(warehouseId: string, documentId: string) {
    const response = await apiClient.post(
      `/checkout/public/${warehouseId}/preview-bol`,
      {
        documentId
      },
      { responseType: 'blob' }
    );
    return response.data;
  },

  async signBol(warehouseId: string, bolData: SignBolData) {
    const response = await apiClient.post(`/checkout/public/${warehouseId}/sign-bol`, bolData);
    return response.data;
  },

  async sendBolEmailSms({
    warehouseId,
    appointmentId,
    contactData,
    type
  }: SendBolEmailSms): Promise<void> {
    const response = await apiClient.post(`/checkout/public/${warehouseId}/send-bol`, {
      appointmentId,
      value: contactData,
      type
    });
    return response.data;
  },

  async completeCheckout(warehouseId: string, checkoutData: CompleteCheckoutData) {
    const body = {
      assetVisitId: checkoutData.assetVisitId,
      ...(checkoutData.appointmentId && { appointmentId: checkoutData.appointmentId }),
      ...(!isEmpty(checkoutData.customFormsData)
        ? { customFormsData: checkoutData.customFormsData }
        : {})
    };
    const response = await apiClient.post(`/checkout/public/${warehouseId}`, body);
    return response.data;
  }
};
